// src/components/features/brands/forms/BrandBasicInfoForm.jsx
import React from 'react';
import { 
  Business as BusinessIcon,
  Category as CategoryIcon 
} from '@mui/icons-material';
import { Autocomplete, Chip, TextField, InputAdornment } from '@mui/material';

// Import shared form components
import { EntityBasicInfoForm } from '../../../components/shared/forms';

// Constants for form options
const INDUSTRY_OPTIONS = [
  { value: 'Luxury', label: 'Luxury' },
  { value: 'Hospitality', label: 'Hospitality' },
  { value: 'Finance', label: 'Finance' },
  { value: 'Technology', label: 'Technology' },
  { value: 'E-commerce', label: 'E-commerce' },
  { value: 'Healthcare', label: 'Healthcare' },
  { value: 'Education', label: 'Education' },
  { value: 'Real Estate', label: 'Real Estate' },
  { value: 'Food & Beverage', label: 'Food & Beverage' },
  { value: 'Automotive', label: 'Automotive' },
  { value: 'Media & Entertainment', label: 'Media & Entertainment' },
  { value: 'Manufacturing', label: 'Manufacturing' },
  { value: 'Retail', label: 'Retail' },
  { value: 'Professional Services', label: 'Professional Services' },
  { value: 'Non-Profit', label: 'Non-Profit' },
  { value: 'Government', label: 'Government' },
  { value: 'Other', label: 'Other' }
];

const STATUS_OPTIONS = [
  { value: 'draft', label: 'Draft' },
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' }
];

/**
 * Brand basic information form component
 * Uses the shared EntityBasicInfoForm component with brand-specific configuration
 */
const BrandBasicInfoForm = ({ 
  brandData, 
  onChange, 
  onNestedChange, 
  clients = [], 
  isEditMode = false,
  errors = {},
  touched = {},
  data, // Support for newer data prop pattern
  requiredFields = []
}) => {
  // Support for both brandData and data prop patterns
  const formData = data || brandData || {};
  
  // Transform nested errors to proper format for EntityBasicInfoForm
  const formattedErrors = {
    ...errors,
    // Handle industry nested fields errors
    industry: {
      ...(errors.industry || {}),
      primary: errors['industry.primary'],
      subCategory: errors['industry.subCategory']
    }
  };
  
  // Transform touched state for nested fields
  const formattedTouched = {
    ...touched,
    // Handle industry nested fields touched state
    industry: {
      ...(touched.industry || {}),
      primary: touched['industry.primary'] || touched.industry?.primary,
      subCategory: touched['industry.subCategory'] || touched.industry?.subCategory
    }
  };
  
  console.log('🌟 BrandBasicInfoForm', {
    requiredFields,
    errors,
    formattedErrors,
    touched,
    formattedTouched
  });
  
  // Generate client options directly using the client objects
  const clientOptions = clients;
  
  // Define the primary-secondary brand options
  const primaryOptions = [
    { value: 'true', label: 'Yes (Main brand for this client)' },
    { value: 'false', label: 'No (Secondary brand)' }
  ];
  
  // Field definitions for the EntityBasicInfoForm - reordered for better business flow
  const fields = [
    // Brand name field
    {
      name: 'name',
      label: 'Brand Name',
      type: 'text',
      required: requiredFields.includes('name'),
      icon: 'business',
      gridSize: { xs: 12, sm: 8 },
      placeholder: 'Enter brand name'
    },
    // Status field
    {
      name: 'status',
      label: 'Status',
      type: 'select',
      required: requiredFields.includes('status'),
      optionsKey: 'status',
      gridSize: { xs: 12, sm: 4 }
    },
    // Primary brand selector
    {
      name: 'isPrimary',
      label: 'Primary Brand',
      type: 'select',
      optionsKey: 'primary',
      gridSize: { xs: 12, sm: 4 },
      helpText: 'Setting as primary will make this the main brand for reports and dashboards'
    },
    // Description field
    {
      name: 'description',
      label: 'Brand Description',
      type: 'multiline',
      rows: 4,
      required: requiredFields.includes('description'),
      icon: 'info',
      gridSize: { xs: 12 },
      placeholder: 'Provide a brief description of the brand, its values, and its market position...'
    },
    // Industry tags field - using direct Autocomplete instead of our wrapper
    {
      name: 'industryTags',
      label: 'Industry Tags',
      type: 'custom',
      icon: 'category',
      gridSize: { xs: 12 },
      renderField: ({ field, error, touched, helpText }) => {
        // Get industry tags from formData
        const industryTags = formData.industry?.tags || [];
        
        // Predefined tag options
        const tagOptions = [
          'Luxury', 'Premium', 'Budget', 'High-end', 'Affordable',
          'Exclusive', 'Sustainable', 'Eco-friendly', 'Traditional', 'Modern',
          'Innovative', 'Conservative', 'Disruptive', 'Established', 'Fast-growing'
        ];
        
        return (
          <Autocomplete
            multiple
            freeSolo
            id="industry-tags"
            options={tagOptions}
            value={industryTags}
            onChange={(event, newValue) => {
              console.log('Industry tags changed:', newValue);
              onNestedChange('industry', 'tags', newValue);
            }}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip
                  key={index}
                  label={option}
                  {...getTagProps({ index })}
                  size="small"
                />
              ))
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="Industry Tags"
                placeholder="Add industry tags and press Enter"
                fullWidth
                helperText="Add keywords that describe the brand's industry positioning"
                InputProps={{
                  ...params.InputProps,
                  startAdornment: (
                    <>
                      <InputAdornment position="start">
                        <CategoryIcon color="action" />
                      </InputAdornment>
                      {params.InputProps.startAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
        );
      }
    }
  ];
  
  // Define the nested fields (industry data)
  const nestedFields = {
    industry: [
      {
        field: 'primary',
        label: 'Industry',
        type: 'select',
        options: INDUSTRY_OPTIONS,
        required: requiredFields.includes('industry.primary'),
        icon: 'category',
        gridSize: { xs: 12, sm: 6 }
      },
      {
        field: 'subCategory',
        label: 'Industry Sub-category',
        type: 'text',
        required: requiredFields.includes('industry.subCategory'),
        gridSize: { xs: 12, sm: 6 },
        placeholder: 'e.g., Boutique Luxury Hotel, Fine Dining, etc.',
        helpText: 'Provide a specific sub-category of the selected industry'
      }
    ]
  };
  
  // Create a custom handler for special fields
  const handleCustomChange = (name, value) => {
    console.log('🔄 handleCustomChange in BrandBasicInfoForm:', { name, value, valueType: typeof value });
    
    if (name === 'industryTags') {
      // For autocomplete with multiple values, the value will already be an array
      // For text input, we need to split by commas
      const tags = Array.isArray(value) 
        ? value 
        : (typeof value === 'string' 
            ? value.split(',').map(tag => tag.trim()).filter(Boolean)
            : []);
      
      onNestedChange('industry', 'tags', tags);
    } else if (name === 'isPrimary') {
      // Convert string 'true'/'false' to boolean
      onChange(name, value === 'true');
    } else if (name === 'clientId') {
      // Ensure clientId is processed correctly and logged
      console.log('🔵 CLIENT ID CHANGE in BrandBasicInfoForm:', { 
        clientId: value,
        valueType: typeof value 
      });
      
      // Always pass the name and value directly through
      onChange(name, value);
    } else {
      onChange(name, value);
    }
  };
  
  // Create relationship field config for client selection - placed at top
  const relationshipField = {
    name: 'clientId',
    label: 'Client',
    required: requiredFields.includes('clientId'),
    disabled: isEditMode,
    options: clientOptions,
    disabledHelperText: isEditMode ? 'Client cannot be changed after brand creation' : '',
    icon: 'person',
    gridSize: { xs: 12, sm: 12 }, // Full width for better visibility
    // Position at the beginning of the form
    position: 'first'
  };
  
  // Support for both data patterns
  const handleChange = onChange?.length === 2 
    ? onChange
    : (e) => {
        const { name, value } = e.target;
        onChange(name, value);
      };
  
  return (
    <EntityBasicInfoForm
      data={formData}
      onChange={handleChange}
      onCustomChange={handleCustomChange}
      onNestedChange={onNestedChange}
      errors={formattedErrors}
      touched={formattedTouched}
      title="Basic Brand Information"
      description="Enter the core information about the brand to help identify and organize it."
      icon={<BusinessIcon />}
      fields={fields}
      options={{
        status: STATUS_OPTIONS,
        primary: primaryOptions
      }}
      relationshipField={relationshipField}
      nestedFields={nestedFields}
    />
  );
};

export default BrandBasicInfoForm;